import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { type FeedbackRound, FeedbackRoundStatus } from '@brafton/feedback-api';
import {
  type ProjectIcon,
  ProgressBar,
  StatusChip,
  pickProjectIcon,
} from '@brafton/feedback-components';

/** A status change requested from a round's row in the listing. */
export interface RoundStatusChange {
  round: FeedbackRound;
  status: FeedbackRoundStatus;
}

/**
 * Feedback-round (project) listing — the table/list view of the Projects page.
 * Presentational: it receives the rounds to display via input and emits a
 * `statusChange` when a row's status chip is changed; the page owns fetching
 * and how (or whether) the change is persisted.
 */
@Component({
  selector: 'app-feedback-round-listing',
  imports: [DatePipe, MatIconModule, StatusChip, ProgressBar],
  templateUrl: './feedback-round-listing.html',
  styleUrl: './feedback-round-listing.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackRoundListing {
  /** The feedback rounds (projects) to display. */
  readonly rounds = input<FeedbackRound[]>([]);

  /**
   * True while the host is fetching projects. With no rows yet, placeholder rows
   * stand in for them (instead of "No projects found"); rows already shown dim.
   */
  readonly loading = input(false);

  /** Placeholder rows shown while the first page loads. */
  protected readonly placeholderRows = [1, 2, 3, 4, 5, 6];

  /** Emitted when a row's status is changed via its status chip. */
  readonly statusChange = output<RoundStatusChange>();

  /** Emitted with the round id when a row is clicked (excluding the status chip). */
  readonly openRound = output<string>();

  /** All selectable round statuses, passed to each row's status chip. */
  protected readonly statuses = Object.values(FeedbackRoundStatus);

  /** A stable, varied icon + color for a round's avatar tile. */
  protected iconFor(round: FeedbackRound): ProjectIcon {
    return pickProjectIcon(round.id);
  }

  protected onStatusChange(round: FeedbackRound, status: string): void {
    this.statusChange.emit({ round, status: status as FeedbackRoundStatus });
  }
}
